<template>
  <div v-if="message?.attachments?.length">
    <div class="p-d-flex p-ai-center">
      <Button @click="toggleFilesOverlay($event, message.id)"
              class="p-button-text">
        <div class="p-d-flex p-ai-center">
          <i class="ti-clip"/>
          <div>{{ $t('Attachments') }}</div>
        </div>
      </Button>
      <div class="p-ml-1 p-badge badge">{{ message.attachments.length }}</div>
    </div>
    <OverlayPanel :ref="'eab-' + message.id" :autoZIndex="$store.state.firstLayerIsOpened">
      <ul>
        <li v-for="(attachment, index) of message.attachments" :key="index">
          <Button @click.prevent="downloadAttachment(attachment)"
                  :disabled="!!downloadingAttachments[attachment.id]"
                  class="p-button-text file-btn">
            <i v-if="isImage(attachment)" class="pi pi-image file-icon"/>
            <i v-else-if="isPdf(attachment)" class="pi pi-file-pdf file-icon"/>
            <i v-else-if="isWord(attachment)" class="pi pi-file-word file-icon"/>
            <i v-else-if="isExcel(attachment)" class="pi pi-file-excel file-icon"/>
            <i v-else class="pi pi-file file-icon"/>
            <span>{{ attachment.source_name }}</span>
            <span v-if="attachment.size">({{ formatSize(attachment.size) }})</span>
          </Button>
        </li>
      </ul>
    </OverlayPanel>
  </div>
</template>

<script>
import settings from "@/settings";
import httpClient from "@/services/http.services";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import formatMixins from "@/mixins/formatMixins";

export default {
  mixins: [ showErrorsMixins, formatMixins ],
  name: 'EmailAttachmentsButton',
  props: {
    message: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      settings,
      downloadingAttachments: {},
    }
  },
  methods: {
    getAttachmentName(att) {
      return (att?.source_name || att?.file_path || '').toLowerCase();
    },
    isImage(att) {
      const mime = att.mime_type || '';
      const name = this.getAttachmentName(att);

      if (mime.startsWith('image/')) return true;

      const exts = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
      return exts.some(ext => name.endsWith(ext));
    },
    isPdf(att) {
      const mime = att.mime_type || '';
      const name = this.getAttachmentName(att);

      return mime === 'application/pdf' || name.endsWith('.pdf');
    },
    isWord(att) {
      const mime = att.mime_type || '';
      const name = this.getAttachmentName(att);

      const wordMimes = [
        'application/msword', // .doc
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      ];

      if (wordMimes.includes(mime)) return true;

      const exts = ['.doc', '.docx'];
      return exts.some(ext => name.endsWith(ext));
    },
    isExcel(att) {
      const mime = att.mime_type || '';
      const name = this.getAttachmentName(att);

      const excelMimes = [
        'application/vnd.ms-excel', // .xls
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      ];

      if (excelMimes.includes(mime)) return true;

      const exts = ['.xls', '.xlsx'];
      return exts.some(ext => name.endsWith(ext));
    },
    async downloadAttachment(attachmentData) {
      const attachmentId = attachmentData?.id;
      if (!attachmentId || this.downloadingAttachments[attachmentId]) return;

      this.downloadingAttachments[attachmentId] = true;

      let objectUrl = null;

      try {
        const { status, data, headers } = await httpClient(
            `email-attachment/download?id=${encodeURIComponent(attachmentId)}`,
            { responseType: 'arraybuffer' }
        );

        if (status !== 200 || !data) {
          throw new Error('Empty response');
        }

        // ----- MIME -----
        const mime =
            headers['content-type'] ||
            attachmentData.mime_type ||
            'application/octet-stream';

        // ----- Filename -----
        let filename =
            attachmentData.source_name ||
            attachmentData.name ||
            'attachment.bin';

        const cd = headers['content-disposition'];
        if (cd) {
          const match = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(cd);
          if (match) {
            try {
              filename = decodeURIComponent(match[1] || match[2]);
            } catch (_) {/**/}
          }
        }

        // ----- Blob -----
        const blob = new Blob([data], { type: mime });
        objectUrl = URL.createObjectURL(blob);

        // ----- Helper: download -----
        const downloadViaLink = () => {
          const a = document.createElement('a');
          a.href = objectUrl;
          a.download = filename;
          a.rel = 'noopener';
          a.style.display = 'none';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        };


        // ---------------------------
        // 1. Типы, которые можно открыть как blob
        // ---------------------------
        const OPENABLE_MIME = [
          // PDF
          'application/pdf',

          // Images
          'image/png',
          'image/jpeg',
          'image/jpg',
          'image/webp',
          'image/gif',
          'image/bmp',
          'image/svg+xml',
          'image/avif',
          'image/apng',

          // Text basic
          'text/plain',
          'text/html',
          'text/css',
          'text/xml',
          'text/csv',
          'text/markdown',
          'text/calendar',
          'text/javascript',

          // Application text-like
          'application/xml',
          'application/json',
          'application/xhtml+xml',
          'application/javascript',
          'application/x-javascript',
          'application/x-yaml'
        ];

        const ext = filename.split('.').pop()?.toLowerCase() || '';

        const OPENABLE_EXT = [
          // pdf
          'pdf',

          // images
          'png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'svg', 'avif', 'apng',

          // text-like
          'txt', 'html', 'css', 'xml', 'json',
          'csv', 'md', 'log', 'ics', 'yaml', 'yml'
        ];

        const canOpenAsBlob =
            OPENABLE_MIME.includes(mime.toLowerCase()) ||
            OPENABLE_EXT.includes(ext);


        // ---------------------------
        // 2. Открываем или скачиваем
        // ---------------------------
        if (canOpenAsBlob) {
          const win = window.open(objectUrl, '_blank');
          if (!win || win.closed || typeof win.closed === 'undefined') {
            downloadViaLink();
          }
        } else {
          downloadViaLink();
        }

      } catch (err) {
        this.showFileNotFoundError();
      } finally {
        delete this.downloadingAttachments[attachmentId];

        if (objectUrl) {
          setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
        }
      }
    },
    toggleFilesOverlay(event, id) {
      if (!event || !id) {
        return false
      }
      this.$refs['eab-' + id.toString()].toggle(event);
    },
  }
}
</script>

<style scoped lang="scss">
ul {
  list-style: none;
  margin: 0;
  padding: 0;
  li {
    padding: 5px 0;
    margin: 0;
  }
}

.badge {
  position: relative;
  right: 12px;
}

.file-btn {
  width: 100%;
  padding: 4px 6px;
  display: inline-flex;
  align-items: center;
  //justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px;
}

//.attachment-thumb {
//  max-width: 40px;
//  max-height: 40px;
//  object-fit: cover;
//  border-radius: 4px;
//}

.file-icon {
  font-size: 14px;
}
</style>